import { type ReactNode, createContext, useContext } from 'react'
import { Platform } from 'react-native'

/** Height of the box the flow is mounted in, once it has been measured. */
const FlowContainerHeightContext = createContext<number | undefined>(undefined)

export function FlowContainerHeightProvider({
  height,
  children,
}: {
  height: number | undefined
  children: ReactNode
}) {
  return (
    <FlowContainerHeightContext.Provider value={height}>
      {children}
    </FlowContainerHeightContext.Provider>
  )
}

/**
 * The height a full-height step should take.
 *
 * A full-height step owns its container, and on native that container is the
 * screen, so the window height names the same box. On web the flow can be
 * mounted in a host box smaller than the window — a dialog — and the window
 * height then overflows it, putting the foot of the step below the host's clip.
 *
 * The measured container is used there instead. It falls back to the window
 * until the first layout lands, which is the same number whenever the flow does
 * own the window.
 */
export function useFullHeightStep(windowHeight: number): number {
  const containerHeight = useContext(FlowContainerHeightContext)
  return Platform.OS === 'web'
    ? (containerHeight ?? windowHeight)
    : windowHeight
}
